import { ReactElement } from 'react';
import { HiOutlineDevicePhoneMobile } from 'react-icons/hi2';
import { cn } from '../../../utils/cn';
import { Episode } from '../../../api/episodes';
import { useSessionEpisodes } from '../../../hooks/useEpisodes';

/**
 * "Nimbus live" chips for the session-details header — one per device
 * with a current (ACTIVE/STALE) episode bound to this session. Colour is
 * the server-derived live_state: live green, overdue amber (a run is
 * open but chunks stopped), idle grey (between runs), stale struck-through.
 * Silent when no episodes exist.
 */

const CHIP_STATES: Record<string, { label: string; className: string }> = {
  live: { label: 'live', className: 'cr-bg-green-100 cr-text-green-800' },
  overdue: { label: 'overdue', className: 'cr-bg-amber-100 cr-text-amber-800' },
  idle: { label: 'idle', className: 'cr-bg-gray-100 cr-text-gray-600' },
  stale: { label: 'stale', className: 'cr-bg-gray-100 cr-text-gray-400 cr-line-through' },
};

/** Server orders newest-first, so the first episode seen per device wins. */
function newestPerDevice(episodes: Episode[]): Episode[] {
  const byDevice = new Map<string, Episode>();
  for (const ep of episodes) {
    const key = ep.device_id || ep.id;
    if (!byDevice.has(key)) byDevice.set(key, ep);
  }
  return Array.from(byDevice.values());
}

export default function NimbusLiveChip({ sessionId }: { sessionId?: string }): ReactElement | null {
  const { data: episodes } = useSessionEpisodes(sessionId);

  if (!episodes?.length) return null;
  const chips = newestPerDevice(episodes).filter((ep) => CHIP_STATES[ep.live_state]);
  if (!chips.length) return null;

  return (
    <>
      {chips.map((ep) => {
        const state = CHIP_STATES[ep.live_state];
        const device = ep.device_id ? ep.device_id.slice(-4) : '????';
        return (
          <span
            key={ep.id}
            title={`Nimbus ${ep.device_id || 'unknown'} — episode ${ep.id.slice(0, 8)} (${ep.live_state})`}
            className={cn(
              'cr-ml-2 cr-inline-flex cr-items-center cr-gap-1 cr-rounded-full cr-px-2 cr-py-0.5',
              'cr-not-italic cr-text-xs cr-font-semibold',
              state.className,
            )}
          >
            <HiOutlineDevicePhoneMobile className="cr-h-3.5 cr-w-3.5" />
            {device} {state.label}
          </span>
        );
      })}
    </>
  );
}
